import {
  collection,
  doc,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  orderBy,
} from 'firebase/firestore';
import type { Unsubscribe } from 'firebase/firestore';
import { ref, uploadBytesResumable } from 'firebase/storage';
import type { UploadTask } from 'firebase/storage';
import { storage, db } from '../firebase';
import type { PendingLessonUpload, PendingUploadStatus } from '../types/lesson';

const PENDING_UPLOADS_COLLECTION = 'pendingLessonUploads';

export type UploadProgressCallback = (progress: number) => void;

export interface CreatePendingUploadParams {
  playerId: string;
  coachId: string;
  offeringId: string;
  offeringTitle: string;
  offeringType?: string;
  totalLessons?: number;
  /** Per-lesson price in cents at time of booking. Always 0 for shot_consult. */
  priceCents: number;
  playerMessage?: string;
  file: File;
  // Payment method
  paymentMethod?: 'stripe' | 'credits';
  // Payment (set for paid offerings before upload begins)
  paymentIntentId?: string;
  /** For credit-paid bookings: the playerCreditTransactions debit doc ID. */
  creditTransactionId?: string;
  // Training program continuation — if set, adds lesson to existing order
  orderId?: string;
  lessonNumber?: number;
  // Passed through to order creation so the Cloud Function sets isRecurringPlan on the new order
  isRecurringPlan?: boolean;
  // Reserved lesson — if set, updates this existing lesson (reserved → in_progress) instead of creating a new one
  existingLessonId?: string;
  // Override the initial status (defaults to 'uploading'). Set to 'payment_pending' when creating
  // the doc before the card charge so retries are blocked even if the upload init later fails.
  initialStatus?: PendingUploadStatus;

  /* Which branch of the booking actually ran: the player's local file, or a
     copy of something already in their cloud. Recorded because the two used
     to be decided by different rules and nothing on the document said which
     had won — the wrong-video bug had to be reconstructed from a fileSize
     that happened to repeat across two bookings. */
  sourceKind?: 'local' | 'cloud';
  /** The cloud file copied, when sourceKind is 'cloud'. */
  sourceCloudFileId?: string;
  /* True when BOTH a local file and a cloud file were selected at submit.
     After this fix that should be impossible, so any document carrying it is
     a live recurrence and not a historical artefact. Cheaper to query than to
     infer, and it is the field that makes the next occurrence findable on the
     day it happens rather than five weeks later. */
  selectionConflict?: boolean;
}

/**
 * Create a pending lesson upload document in Firestore
 * This stores the lesson metadata that will be used when the lesson is created after processing
 */
export async function createPendingUpload(
  params: CreatePendingUploadParams
): Promise<string> {
  const { file, priceCents, playerMessage, paymentMethod, paymentIntentId, creditTransactionId, offeringType, totalLessons, orderId, lessonNumber, isRecurringPlan, existingLessonId, initialStatus = 'uploading', sourceKind, sourceCloudFileId, selectionConflict, ...lessonMetadata } = params;

  const uploadRef = doc(collection(db, PENDING_UPLOADS_COLLECTION));
  const uploadId = uploadRef.id;

  const tempStoragePath = `temp/lesson-uploads/${uploadId}/video.mp4`;
  const now = new Date().toISOString();

  const pendingUpload: Record<string, unknown> = {
    id: uploadId,
    ...lessonMetadata,
    priceCents,
    originalFileName: file.name,
    fileSize: file.size,
    tempStoragePath,
    status: initialStatus,
    uploadProgress: 0,
    retryCount: 0,
    createdAt: now,
    updatedAt: now,
  };

  if (playerMessage !== undefined) pendingUpload.playerMessage = playerMessage;
  if (paymentMethod !== undefined) pendingUpload.paymentMethod = paymentMethod;
  if (creditTransactionId !== undefined) {
    pendingUpload.creditTransactionId = creditTransactionId;
    pendingUpload.paymentStatus = 'captured';
  }
  if (paymentIntentId !== undefined) {
    pendingUpload.paymentIntentId = paymentIntentId;
    // Only mark as captured once the charge is confirmed; payment_pending means the PI exists
    // but the card has not been charged yet (or charge status is unknown).
    if (initialStatus !== 'payment_pending') {
      pendingUpload.paymentStatus = 'captured';
    }
  }
  if (sourceKind !== undefined) pendingUpload.sourceKind = sourceKind;
  if (sourceCloudFileId !== undefined) {
    pendingUpload.sourceCloudFileId = sourceCloudFileId;
  }
  // Only written when true, so the field's presence IS the alert.
  if (selectionConflict) pendingUpload.selectionConflict = true;
  if (offeringType !== undefined) pendingUpload.offeringType = offeringType;
  if (totalLessons !== undefined) pendingUpload.totalLessons = totalLessons;
  if (orderId !== undefined) pendingUpload.orderId = orderId;
  if (lessonNumber !== undefined) pendingUpload.lessonNumber = lessonNumber;
  if (isRecurringPlan) pendingUpload.isRecurringPlan = true;
  if (existingLessonId !== undefined) pendingUpload.existingLessonId = existingLessonId;

  await setDoc(uploadRef, pendingUpload);

  return uploadId;
}

/**
 * Update the upload progress in Firestore
 * Throttled to avoid excessive writes
 */
let lastProgressUpdate = 0;
const PROGRESS_UPDATE_THROTTLE_MS = 3000; // 3s to reduce emulator load

export async function updateUploadProgress(
  uploadId: string,
  progress: number
): Promise<void> {
  const now = Date.now();

  // Throttle progress updates (except for 0% and 100%)
  if (progress > 0 && progress < 100 && now - lastProgressUpdate < PROGRESS_UPDATE_THROTTLE_MS) {
    return;
  }

  lastProgressUpdate = now;

  const uploadRef = doc(db, PENDING_UPLOADS_COLLECTION, uploadId);
  await updateDoc(uploadRef, {
    uploadProgress: Math.round(progress),
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Update the upload status
 */
export async function updateUploadStatus(
  uploadId: string,
  status: PendingUploadStatus,
  additionalData?: Partial<PendingLessonUpload>
): Promise<void> {
  const uploadRef = doc(db, PENDING_UPLOADS_COLLECTION, uploadId);

  const updates: Record<string, unknown> = {
    status,
    updatedAt: new Date().toISOString(),
    ...additionalData,
  };

  // Add timestamp for specific status transitions
  if (status === 'uploaded') {
    updates.uploadedAt = new Date().toISOString();
  } else if (status === 'processing') {
    updates.processingStartedAt = new Date().toISOString();
  } else if (status === 'completed') {
    updates.completedAt = new Date().toISOString();
  }

  await updateDoc(uploadRef, updates);
}

/**
 * Mark upload as failed with error details
 */
export async function markUploadFailed(
  uploadId: string,
  errorMessage: string,
  errorCode: PendingLessonUpload['errorCode'] = 'upload_failed'
): Promise<void> {
  const uploadRef = doc(db, PENDING_UPLOADS_COLLECTION, uploadId);

  // Get current retry count
  const uploadDoc = await getDoc(uploadRef);
  const currentRetryCount = uploadDoc.exists() ? (uploadDoc.data().retryCount || 0) : 0;

  await updateDoc(uploadRef, {
    status: 'failed',
    errorMessage,
    errorCode,
    retryCount: currentRetryCount + 1,
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Dismiss a pending upload (for failed or stale uploads)
 * Marks as 'dismissed' so it no longer appears in UI; Cloud Functions handle actual cleanup
 *
 * For a teardown the PLAYER did not ask for, use abandonUploadOnError instead.
 * Both hide the row; only one of them is honest about who acted.
 */
export async function dismissUpload(uploadId: string): Promise<void> {
  const uploadRef = doc(db, PENDING_UPLOADS_COLLECTION, uploadId);
  await updateDoc(uploadRef, {
    status: 'dismissed',
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Tear down a pending upload after a booking threw.
 *
 * Same effect on the UI as dismissUpload — the row goes away and the player
 * can retry — but recorded as its own terminal state with the exception
 * attached, so a failure is not filed as a change of mind. The
 * cancelled-intent bug survived three weeks and three players precisely
 * because it was indistinguishable from someone walking away.
 */
export async function abandonUploadOnError(
  uploadId: string,
  error: unknown,
): Promise<void> {
  const uploadRef = doc(db, PENDING_UPLOADS_COLLECTION, uploadId);
  const message = error instanceof Error
    ? error.message
    : (typeof error === 'string' ? error : 'Unknown error');
  await updateDoc(uploadRef, {
    status: 'abandoned_error',
    // Capped: this is a diagnostic breadcrumb, not a log sink.
    errorMessage: message.slice(0, 500),
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Check if an upload is stale (no progress for too long).
 * - 'uploading': stale after 5 minutes without a progress update (stuck mid-transfer)
 * - 'uploaded'/'processing': stale after timeoutMinutes (server-side processing hung)
 *
 * The Cloud Run Job task-timeout is 3 hours, so the default here matches that
 * ceiling. The job writes updatedAt heartbeats at key milestones; if those
 * stop arriving for 3 hours the job has genuinely hung.
 */
export function isUploadStale(upload: PendingLessonUpload, timeoutMinutes = 180): boolean {
  const elapsed = Date.now() - new Date(upload.updatedAt).getTime();

  if (upload.status === 'uploading') {
    return elapsed > 5 * 60 * 1000;
  }
  if (upload.status === 'uploaded' || upload.status === 'processing') {
    return elapsed > timeoutMinutes * 60 * 1000;
  }
  return false;
}

/**
 * Mark a stale upload as failed
 */
export async function markStaleUploadAsFailed(uploadId: string): Promise<void> {
  await markUploadFailed(
    uploadId,
    'Upload timed out - processing took too long. Please try again.',
    'processing_failed'
  );
}

/**
 * Retry a failed upload by resetting the existing Firestore doc and restarting
 * the upload to the same storage path.
 *
 * Use this instead of creating a new upload when payment was already captured —
 * it preserves the paymentIntentId on the original doc so the Cloud Function
 * can still create the lesson when the upload eventually completes.
 */
export async function retryUpload(
  uploadId: string,
  file: File,
  onProgress?: UploadProgressCallback
): Promise<{ cancel: () => void }> {
  const uploadRef = doc(db, PENDING_UPLOADS_COLLECTION, uploadId);
  const uploadDoc = await getDoc(uploadRef);
  if (!uploadDoc.exists()) throw new Error('Upload not found');

  const currentRetryCount = (uploadDoc.data().retryCount ?? 0) as number;

  await updateDoc(uploadRef, {
    status: 'uploading',
    uploadProgress: 0,
    retryCount: currentRetryCount + 1,
    updatedAt: new Date().toISOString(),
  });

  const { cancel } = startUpload(uploadId, file, onProgress);
  return { cancel };
}

/**
 * Start the video upload to Firebase Storage
 * Returns a cleanup function to cancel the upload
 */
export function startUpload(
  uploadId: string,
  file: File,
  onProgress?: UploadProgressCallback
): { uploadTask: UploadTask; cancel: () => void } {
  const tempStoragePath = `temp/lesson-uploads/${uploadId}/video.mp4`;
  const storageRef = ref(storage, tempStoragePath);

  const uploadTask = uploadBytesResumable(storageRef, file);

  uploadTask.on(
    'state_changed',
    (snapshot) => {
      const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;

      // Update local callback
      onProgress?.(progress);

      // Update Firestore (throttled)
      updateUploadProgress(uploadId, progress).catch((err) => {
        console.error('Failed to update progress in Firestore:', err);
      });
    },
    async (error) => {
      console.error('Upload error:', error);
      await markUploadFailed(uploadId, error.message, 'upload_failed');
    },
    async () => {
      // Upload completed successfully
      try {
        await updateUploadStatus(uploadId, 'uploaded', {
          uploadProgress: 100,
        });
      } catch (err) {
        console.error('Failed to update status after upload:', err);
      }
    }
  );

  return {
    uploadTask,
    cancel: () => {
      uploadTask.cancel();
      markUploadFailed(uploadId, 'Upload cancelled by user', 'upload_failed');
    },
  };
}

/**
 * Get a pending upload by ID
 */
export async function getPendingUpload(
  uploadId: string
): Promise<PendingLessonUpload | null> {
  const uploadRef = doc(db, PENDING_UPLOADS_COLLECTION, uploadId);
  const uploadDoc = await getDoc(uploadRef);

  if (!uploadDoc.exists()) {
    return null;
  }

  return uploadDoc.data() as PendingLessonUpload;
}

/**
 * Subscribe to a specific upload's status changes
 */
export function subscribeToUpload(
  uploadId: string,
  callback: (upload: PendingLessonUpload | null) => void
): Unsubscribe {
  const uploadRef = doc(db, PENDING_UPLOADS_COLLECTION, uploadId);

  return onSnapshot(uploadRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(snapshot.data() as PendingLessonUpload);
    } else {
      callback(null);
    }
  });
}

/**
 * Subscribe to all pending uploads for a user
 */
export function subscribeToUserUploads(
  userId: string,
  callback: (uploads: PendingLessonUpload[]) => void
): Unsubscribe {
  const uploadsRef = collection(db, PENDING_UPLOADS_COLLECTION);
  const q = query(
    uploadsRef,
    where('playerId', '==', userId),
    where('status', 'in', ['uploading', 'uploaded', 'processing', 'failed']),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const uploads = snapshot.docs.map((doc) => doc.data() as PendingLessonUpload);
    callback(uploads);
  });
}

/**
 * Check if there's already a pending upload for this offering
 * Prevents duplicate uploads while one is still in progress, and prevents a second
 * charge when the upload failed after payment was already captured.
 *
 * Returns:
 *  - An active (non-failed) upload doc → caller should block and show "in progress" error
 *  - A failed upload doc that has a captured payment → caller should block and prompt retry
 *  - null → no blocker; caller may proceed with a new booking
 */
export async function checkExistingPendingUpload(
  playerId: string,
  coachId: string,
  offeringId: string
): Promise<PendingLessonUpload | null> {
  const uploadsRef = collection(db, PENDING_UPLOADS_COLLECTION);
  const q = query(
    uploadsRef,
    where('playerId', '==', playerId),
    where('coachId', '==', coachId),
    where('offeringId', '==', offeringId),
    // 'payment_pending' is included so a doc written before the card charge blocks retries even
    // if the upload never started (e.g. the charge succeeded but initiateLessonUpload threw).
    // 'failed' is included so uploads that failed AFTER payment was captured also block a second charge.
    where('status', 'in', ['payment_pending', 'uploading', 'uploaded', 'processing', 'failed'])
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const docs = snapshot.docs.map((d) => d.data() as PendingLessonUpload);

  // Prioritize active (non-failed) uploads — always block these.
  const active = docs.find((d) => d.status !== 'failed');
  if (active) return active;

  // For failed uploads: only block if payment was already captured.
  // A failed free-consult upload (no payment) should not prevent rebooking.
  return docs.find((d) =>
    d.status === 'failed' && !!(d.paymentIntentId || d.creditTransactionId)
  ) ?? null;
}

/**
 * Main function to initiate an async lesson video upload
 * Creates the Firestore document and starts the upload
 */
export async function initiateLessonUpload(
  params: CreatePendingUploadParams,
  onProgress?: UploadProgressCallback
): Promise<{
  uploadId: string;
  cancel: () => void;
}> {
  // Create the pending upload document
  const uploadId = await createPendingUpload(params);

  // Start the upload
  const { cancel } = startUpload(uploadId, params.file, onProgress);

  return { uploadId, cancel };
}
